import { useState, useEffect, useRef } from 'react';
import { Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMarketRegion } from '@/hooks/useMarketRegion';
import { supabase } from '@/integrations/supabase/client';

const VIDEO_URLS = {
  BR: 'https://www.youtube.com/embed/QMjJ9wYIRpA',
  US: 'https://www.youtube.com/embed/iyNxpbEwgxs'
} as const;

const THUMBNAIL_URLS = {
  BR: 'https://img.youtube.com/vi/QMjJ9wYIRpA/maxresdefault.jpg',
  US: 'https://img.youtube.com/vi/iyNxpbEwgxs/maxresdefault.jpg'
} as const;

export const ExplainerVideo = () => {
  const { t } = useTranslation();
  const { region, loading } = useMarketRegion();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);

  const videoUrl = VIDEO_URLS[region];
  const thumbnailUrl = THUMBNAIL_URLS[region];

  // Track video impression when it enters viewport
  useEffect(() => {
    if (loading || hasTrackedView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView) {
          supabase.functions.invoke('track-video-event', {
            body: {
              event: 'explainer_video_viewed',
              region,
              video_url: videoUrl
            }
          });
          setHasTrackedView(true);
        }
      },
      { threshold: 0.5 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasTrackedView, region, videoUrl]);

  const handlePlay = () => {
    setIsPlaying(true);
    
    // Track play event (dispara notificação Slack server-side)
    supabase.functions.invoke('track-video-event', {
      body: {
        event: 'explainer_video_play',
        region,
        video_url: videoUrl
      }
    });
  };

  if (loading) {
    return (
      <div className="w-full aspect-video rounded-xl bg-muted animate-pulse" />
    );
  }

  return (
    <div 
      ref={videoRef}
      className="relative w-full aspect-video rounded-xl overflow-hidden group shadow-newsletter border border-newsletter-primary/20 hover:border-newsletter-primary/40 transition-all duration-300"
    >
      {!isPlaying ? (
        <>
          {/* Thumbnail */}
          <img
            src={thumbnailUrl}
            alt={t('explainerVideo.title')}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          {/* Play button */}
          <button
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center group cursor-pointer"
            aria-label={t('explainerVideo.playButton')}
          >
            <div className="w-20 h-20 rounded-full bg-newsletter-primary/90 backdrop-blur-sm flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <Play className="w-10 h-10 text-white fill-white ml-1" />
            </div>
          </button>

          {/* Duration badge */}
          <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm text-white text-sm font-medium">
            {t('explainerVideo.duration')}
          </div>
        </>
      ) : (
        <iframe
          src={`${videoUrl}?autoplay=1&rel=0&modestbranding=1`}
          title={t('explainerVideo.title')}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      )}
    </div>
  );
};
